import React, { useState } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  Avatar, 
  Modal, 
  Tooltip,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Input
} from 'antd';
import { 
  CheckOutlined, 
  CloseOutlined, 
  EyeOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CrownOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import TabListLoading from '../../../components/Common/TabListLoading';
import { BootcampApplicationResponse } from '../../../types';

const { Title, Text } = Typography;
const { TextArea } = Input;

const BootcampMeetingsTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selectedApplication, setSelectedApplication] = useState<any | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [mentorNotes, setMentorNotes] = useState('');

  const { data: applications = [], isLoading: loading } = useQuery({
    queryKey: mentorProfileQueryKeys.mentorBootcampApplications,
    queryFn: async () => {
      const res = await apiService.getMentorBootcampApplications();
      if (!res.success) throw new Error(res.message || 'Bootcamp başvuruları yüklenirken hata oluştu');
      return res.data;
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.mentorBootcampApplications });
  };

  const updateStatusMutation = useMutation({
    mutationFn: ({ applicationId, status, notes }: { applicationId: number; status: string; notes?: string }) =>
      apiService.updateBootcampApplicationStatus(applicationId, status, notes),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Başvuru güncellendi');
        invalidate();
      } else toast.error(res.message || 'Başvuru güncellenirken hata oluştu');
    },
    onError: () => toast.error('Başvuru güncellenirken hata oluştu'),
  });

  const completeMutation = useMutation({
    mutationFn: (applicationId: number) => apiService.completeBootcampApplication(applicationId),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Bootcamp tamamlandı olarak işaretlendi');
        invalidate();
      } else toast.error(res.message || 'Bootcamp tamamlanırken hata oluştu');
    },
    onError: () => toast.error('Bootcamp tamamlanırken hata oluştu'),
  });

  const handleApprove = (applicationId: number) => updateStatusMutation.mutate({ applicationId, status: 'Approved' });
  const handleReject = (applicationId: number) => updateStatusMutation.mutate({ applicationId, status: 'Rejected' });
  const handleComplete = (applicationId: number) => completeMutation.mutate(applicationId);

  const handleViewDetails = (application: any) => {
    setSelectedApplication(application);
    setDetailModalVisible(true);
  };

  const handleReview = (application: any) => {
    setSelectedApplication(application);
    setMentorNotes(application.mentorNotes || '');
    setReviewModalVisible(true);
  };

  const handleSubmitReview = () => {
    if (!selectedApplication) return;
    updateStatusMutation.mutate(
      { applicationId: selectedApplication.id, status: selectedApplication.status, notes: mentorNotes },
      {
        onSuccess: () => {
          setReviewModalVisible(false);
          setMentorNotes('');
        },
      }
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending': return 'orange';
      case 'Approved': return 'blue';
      case 'Rejected': return 'red';
      case 'Completed': return 'green';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Pending': return 'Beklemede';
      case 'Approved': return 'Onaylandı';
      case 'Rejected': return 'Reddedildi';
      case 'Completed': return 'Tamamlandı';
      default: return status;
    }
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    {
      title: 'Mentee',
      key: 'mentee',
      width: 200,
      render: (record: any) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            size={40} 
            icon={<UserOutlined />}
            style={{ backgroundColor: '#722ed1', marginRight: '12px' }}
          />
          <div>
            <div style={{ fontWeight: '500' }}>
              {record.menteeName}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.menteeEmail}
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Bootcamp',
      dataIndex: 'bootcampTitle',
      key: 'bootcamp',
      width: 200,
      render: (title: string) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CrownOutlined style={{ marginRight: '8px', color: '#722ed1' }} />
          <Text strong>{title}</Text>
        </div>
      )
    },
    {
      title: 'Başvuru Tarihi',
      dataIndex: 'appliedAt',
      key: 'appliedAt',
      width: 150,
      render: (date: string) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CalendarOutlined style={{ marginRight: '4px', color: 'var(--primary-500)' }} />
          <Text>{formatDateTime(date).split(' ')[0]}</Text>
        </div>
      )
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: 'Değerlendirme',
      key: 'review',
      width: 120,
      render: (record: any) => (
        <div>
          {record.isReviewMentee && (
            <Tag color="green">Mentee Değerlendirdi</Tag>
          )}
          {record.isReviewMentor && (
            <Tag color="blue">Mentor Değerlendirdi</Tag>
          )}
          {!record.isReviewMentee && !record.isReviewMentor && (
            <Tag color="orange">Beklemede</Tag>
          )}
        </div>
      )
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 200,
      render: (record: any) => (
        <Space>
          <Tooltip title="Detayları Görüntüle">
            <Button 
              type="text" 
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Değerlendir">
            <Button 
              type="text" 
              icon={<FileTextOutlined />}
              onClick={() => handleReview(record)}
            />
          </Tooltip>
          {record.status === 'Pending' && (
            <>
              <Tooltip title="Onayla">
                <Button 
                  type="text" 
                  icon={<CheckOutlined />}
                  style={{ color: 'var(--success)' }}
                  onClick={() => handleApprove(record.id)}
                />
              </Tooltip>
              <Tooltip title="Reddet">
                <Button 
                  type="text" 
                  icon={<CloseOutlined />}
                  style={{ color: 'var(--error)' }}
                  onClick={() => handleReject(record.id)}
                />
              </Tooltip>
            </>
          )}
          {record.status === 'Approved' && (
            <Tooltip title="Tamamlandı Olarak İşaretle">
              <Button 
                type="text" 
                icon={<CheckOutlined />}
                style={{ color: 'var(--primary-500)' }}
                onClick={() => handleComplete(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  // İstatistikler
  const totalApplications = applications.length;
  const pendingApplications = applications.filter(a => a.status === 'Pending').length;
  const approvedApplications = applications.filter(a => a.status === 'Approved').length;
  const completedApplications = applications.filter(a => a.status === 'Completed').length;

  return (
    <div>
      {/* İstatistikler */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Başvuru"
              value={totalApplications}
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Bekleyen"
              value={pendingApplications}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Onaylanan"
              value={approvedApplications}
              prefix={<CheckOutlined />}
              valueStyle={{ color: 'var(--success)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tamamlanan"
              value={completedApplications}
              prefix={<CheckOutlined />}
              valueStyle={{ color: 'var(--primary-500)' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Başvuru Listesi */}
      <TabListLoading loading={loading}>
      <Card title="Bootcamp Başvuruları">
        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={applications}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} başvuru`
          }}
        />
      </Card>
      </TabListLoading>

      {/* Detay Modal */}
      <Modal
        title="Bootcamp Başvuru Detayları"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedApplication && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <Avatar 
                    size={60} 
                    icon={<UserOutlined />}
                    style={{ backgroundColor: '#722ed1', marginRight: '16px' }}
                  />
                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      {selectedApplication.menteeName}
                    </Title>
                    <Text type="secondary">{selectedApplication.menteeEmail}</Text>
                  </div>
                </div>
              </Col>
              
              <Col span={24}>
                <Text strong>Bootcamp:</Text>
                <br />
                <Text>{selectedApplication.bootcampTitle}</Text>
              </Col>
              
              <Col span={12}>
                <Text strong>Başvuru Tarihi:</Text>
                <br />
                <Text>{formatDateTime(selectedApplication.appliedAt)}</Text>
              </Col>
              <Col span={12}>
                <Text strong>Durum:</Text>
                <br />
                <Tag color={getStatusColor(selectedApplication.status)}>
                  {getStatusText(selectedApplication.status)}
                </Tag>
              </Col>
              
              {selectedApplication.coverLetter && (
                <Col span={24}>
                  <Text strong>Başvuru Mektubu:</Text>
                  <br />
                  <Text>{selectedApplication.coverLetter}</Text>
                </Col>
              )}
              
              {selectedApplication.mentorNotes && (
                <Col span={24}>
                  <Text strong>Mentor Notları:</Text>
                  <br />
                  <Text>{selectedApplication.mentorNotes}</Text>
                </Col>
              )}
              
              <Col span={24}>
                <Text strong>Değerlendirme Durumu:</Text>
                <br />
                <Space>
                  {selectedApplication.isReviewMentee && (
                    <Tag color="green">Mentee Değerlendirdi</Tag>
                  )}
                  {selectedApplication.isReviewMentor && (
                    <Tag color="blue">Mentor Değerlendirdi</Tag>
                  )}
                  {!selectedApplication.isReviewMentee && !selectedApplication.isReviewMentor && (
                    <Tag color="orange">Beklemede</Tag>
                  )}
                </Space>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Değerlendirme Modal */}
      <Modal
        title="Bootcamp Değerlendirmesi"
        open={reviewModalVisible}
        onOk={handleSubmitReview}
        onCancel={() => {
          setReviewModalVisible(false);
          setMentorNotes('');
        }}
        width={500}
      >
        {selectedApplication && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Mentee:</Text> {selectedApplication.menteeName}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Bootcamp:</Text> {selectedApplication.bootcampTitle}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Mentor Notları:</Text>
              <TextArea
                value={mentorNotes}
                onChange={(e) => setMentorNotes(e.target.value)}
                placeholder="Mentor notlarınızı buraya yazın..."
                rows={4}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BootcampMeetingsTab;
